import { Menu } from 'lucide-react';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useT } from '@/lib/i18n';

/**
 * `(min-width: 70em)` — 1120 px at the default text size — where the header switches to the jump-link pill
 * (`styles/components/header.css`; D-319). In `em` so that larger browser text moves it with the text (WCAG 1.4.4).
 */
const DESKTOP_QUERY = '(min-width: 70em)';

// The panel (Radix Dialog: focus trap, scroll lock, dismissable layer …) is a separate chunk. It is
// prefetched on hover/focus of the button, so by the time of the click it is usually already there.
const loadPanel = () => import('./MobileMenuPanel');
const MobileMenuPanel = lazy(() =>
  loadPanel().then((module) => ({ default: module.MobileMenuPanel })),
);

/**
 * Hamburger button (below 70em) + the full-screen menu dialog (see `MobileMenuPanel`).
 *
 * The open state is tied to the router location key, so any navigation — link click, browser back —
 * closes the menu without an effect. Growing into the desktop layout closes it too.
 * `Shop` is deliberately NOT inside the menu: it stays in the header bar.
 */
export function MobileMenu() {
  const t = useT();
  const location = useLocation();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);
  // Once opened, the panel stays mounted (closed) so Radix can play its exit animation.
  const [requested, setRequested] = useState(false);
  const open = openKey === location.key;

  const setOpen = (next: boolean) => {
    setOpenKey(next ? location.key : null);
  };

  useEffect(() => {
    const query = window.matchMedia(DESKTOP_QUERY);
    const onChange = (event: MediaQueryListEvent) => {
      if (event.matches) setOpenKey(null);
    };
    query.addEventListener('change', onChange);
    return () => {
      query.removeEventListener('change', onChange);
    };
  }, []);

  const prefetch = () => {
    // best effort: if the chunk cannot be fetched now, the click will retry (and surface the error)
    void loadPanel().catch(() => undefined);
  };

  return (
    <>
      <Button
        ref={triggerRef}
        variant="icon"
        className="site-header__menu"
        aria-label={t('nav.openMenu')}
        aria-haspopup="dialog"
        aria-expanded={open}
        onPointerEnter={prefetch}
        onFocus={prefetch}
        onClick={() => {
          setRequested(true);
          setOpen(true);
        }}
      >
        <Menu size={22} aria-hidden="true" />
      </Button>

      {requested ? (
        <Suspense
          fallback={
            <div
              className="menu-panel grid place-items-center"
              role="status"
              aria-label={t('common.loading')}
            >
              <Spinner size={28} />
            </div>
          }
        >
          <MobileMenuPanel open={open} onOpenChange={setOpen} returnFocusRef={triggerRef} />
        </Suspense>
      ) : null}
    </>
  );
}
